'use client';

import { useEffect, useRef, useState } from 'react';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { InstallmentCalculatorDialog } from '@packages/installment-calculator/src/InstallmentCalculatorDialog/InstallmentCalculatorDialog.client';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { SideBySideWithTipBadge } from '../layouts/SideBySideWithTipBadge/SideBySideWithTipBadge.client';
import { OutlinedWithTipBadge } from '../layouts/OutlinedWithTipBadge/OutlinedWithTipBadge.client';
import { IconInstallmentsAndLegalHints } from '../layouts/IconInstallmentsAndLegalHints/IconInstallmentsAndLegalHints.client';
import { SideBySideWithIconBadge } from '../layouts/SideBySideWithIconBadge/SideBySideWithIconBadge.client';

export type InstallmentCalculatorInDialogProps = {
  /**
   * the single installment value from the calculation result as integer (e.g. 1337)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * Product price value as int (e.g. 1337)
   */
  basePrice: number;
};

export const InstallmentCalculatorInDialog = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  basePrice,
}: InstallmentCalculatorInDialogProps) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const {
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
  } = useConfig();

  const { trackGlycerinDisplayLink } = useInstallmentCalculatorTracking();

  const trackedOnce = useRef(false);

  useEffect(() => {
    if (!trackedOnce.current) {
      trackGlycerinDisplayLink();
      trackedOnce.current = true;
    }
  }, [trackGlycerinDisplayLink]);

  const handleOpenDialog = () => {
    setDialogOpened(true);
  };

  return (
    <>
      <InstallmentCalculatorDialog
        basePrice={basePrice}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
      />
      {(() => {
        switch (InstallmentCalculatorCallToActionLayout?.layout) {
          case 'OutlinedWithTipBadge': {
            return (
              <OutlinedWithTipBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalculatorHandler={handleOpenDialog}
              />
            );
          }
          case 'SideBySideWithTipBadge': {
            return (
              <SideBySideWithTipBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalculatorHandler={handleOpenDialog}
              />
            );
          }
          case 'SideBySideWithIconBadge': {
            return (
              <SideBySideWithIconBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalculatorHandler={handleOpenDialog}
              />
            );
          }
          case 'IconInstallmentsAndLegalHints': {
            return (
              <IconInstallmentsAndLegalHints
                singleInstallment={singleInstallment}
                currency={currency}
                openInstallmentCalculatorHandler={handleOpenDialog}
              />
            );
          }
          default:
            return null;
        }
      })()}
    </>
  );
};
